"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material";

// Renders a recruiter-supplied third-party reviews embed (Sourcr / Google
// Reviews) inside a sandboxed iframe. The embed is a chunk of HTML that
// contains its own <script>. Previously we injected that script directly into
// the React-managed document, where the vendor code would walk the DOM and
// JSON.stringify a React-rendered <a> element, hitting React's internal fiber
// cycle and crashing the whole page (Sentry FLOATS-17B). Running it inside a
// sandbox WITHOUT allow-same-origin puts the widget in its own opaque origin,
// so it can only see its own document and can never reach the host page.

const MESSAGE_TAG = "__floatsReviewsWidget";

// Runs inside the iframe. Reports its content height to the parent so we can
// size the frame (the frame is cross-origin, so the parent can't measure it
// directly). Must not contain the literal "</script>" sequence.
const RESIZE_SCRIPT = `
(function () {
  function post() {
    try {
      var doc = document;
      var h = Math.max(
        doc.documentElement ? doc.documentElement.scrollHeight : 0,
        doc.body ? doc.body.scrollHeight : 0
      );
      if (h > 0) {
        parent.postMessage({ ${MESSAGE_TAG}: true, height: h }, "*");
      }
    } catch (e) {}
  }
  window.addEventListener("load", post);
  window.addEventListener("resize", post);
  try {
    if (window.ResizeObserver && document.body) {
      new ResizeObserver(post).observe(document.body);
    }
  } catch (e) {}
  // The widget renders asynchronously; re-measure a few times after load.
  [100, 400, 900, 1800, 3000].forEach(function (t) { setTimeout(post, t); });
})();
`;

const buildSrcDoc = (widgetHtml) =>
  `<!doctype html><html><head><meta charset="utf-8">` +
  `<meta name="color-scheme" content="light"><base target="_blank">` +
  `<style>html,body{margin:0;padding:0;background:transparent;}` +
  `body{overflow:hidden;}</style></head><body>` +
  `${widgetHtml}<script>${RESIZE_SCRIPT}</script></body></html>`;

const Frame = styled("iframe")({
  display: "block",
  width: "100%",
  border: "none",
  colorScheme: "normal",
});

export default function ReviewWidgetFrame({
  html,
  title = "Reviews",
  minHeight = 80,
  sx,
}) {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(minHeight);

  const srcDoc = useMemo(() => buildSrcDoc(html), [html]);

  useEffect(() => {
    const handleMessage = (event) => {
      // The sandbox has no allow-same-origin, so event.origin is "null";
      // authenticate by source window + our message tag instead.
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || data[MESSAGE_TAG] !== true) return;
      if (typeof data.height === "number" && data.height > 0) {
        setHeight(Math.max(minHeight, Math.ceil(data.height)));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [minHeight]);

  return (
    <Frame
      ref={frameRef}
      title={title}
      srcDoc={srcDoc}
      // allow-scripts lets the widget run; the deliberate absence of
      // allow-same-origin is what isolates it from the host page. Popups let
      // review links open in a new tab.
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      loading="lazy"
      style={{ height }}
      sx={sx}
    />
  );
}
